import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setCredentials, logout } from "../store/slices/authSlice";

// Mutex for token refresh to prevent race conditions
let isRefreshing = false;
let refreshPromise = null;

// Base query with credentials for cookie-based auth
// In development, use relative path with proxy (same-origin for cookies)
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "http://localhost:5045/api/v1",
  credentials: "include", // Send cookies with every request
});

// Perform token refresh with mutex lock
const performTokenRefresh = async (api, extraOptions) => {
  // If already refreshing, wait for the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Start refresh
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // Refresh token is in HttpOnly cookie, no need to send in body
      const refreshResult = await baseQuery(
        {
          url: "/auth/refresh-token",
          method: "POST",
        },
        api,
        extraOptions
      );

      if (refreshResult.data && refreshResult.data.success) {
        // Update Redux state with new user/company data
        api.dispatch(setCredentials(refreshResult.data.data));
        return true;
      } else {
        // Refresh failed, logout user
        api.dispatch(logout());
        window.location.href = "/#/login";
        return false;
      }
    } catch (error) {
      // Refresh failed, logout user
      api.dispatch(logout());
      window.location.href = "/#/login";
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Base query with automatic token refresh on 401
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Try the original request
  let result = await baseQuery(args, api, extraOptions);

  // If we get 401, try to refresh the token
  if (result.error && result.error.status === 401) {
    const refreshed = await performTokenRefresh(api, extraOptions);

    if (refreshed) {
      // Retry the original request with new token (in cookie)
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

// Define the API service
export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["User", "Auth", "Admin", "QuestionBank", "FormTemplate", "Submission", "DiagnosticTemplate", "DiagnosticAssignment", "DiagnosticSubmission", "LearningResource", "DevelopmentPlan", "CompetencySet", "CompetencyRubric", "CommunityEvent", "RSVP", "CompanyPosition"],
  endpoints: (builder) => ({
    // ==================== AUTH ENDPOINTS ====================

    login: builder.mutation({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Auth"],
    }),

    register: builder.mutation({
      query: (userData) => ({
        url: "/auth/register",
        method: "POST",
        body: userData,
      }),
      transformResponse: (response) => response.data,
    }),

    // Company Registration - Owner creates new company
    registerWithCompany: builder.mutation({
      query: ({ user, company }) => ({
        url: "/auth/register-with-company",
        method: "POST",
        body: { user, company },
      }),
      transformResponse: (response) => response.data,
    }),

    // Join Company with Invite Code
    joinWithCode: builder.mutation({
      query: ({ user, inviteCode, positionId }) => ({
        url: "/auth/register-with-code",
        method: "POST",
        body: { user, inviteCode, positionId },
      }),
      transformResponse: (response) => response.data,
    }),

    // Validate Invite Code
    validateInviteCode: builder.query({
      query: (code) => ({
        url: `/company/validate-code/${code}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data,
    }),

    // Create Company for authenticated user (no company yet)
    createCompanyForUser: builder.mutation({
      query: (companyData) => ({
        url: "/company",
        method: "POST",
        body: companyData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["User", "Auth"],
    }),

    // Join Company with invite code for authenticated user (no company yet)
    joinCompanyForUser: builder.mutation({
      query: ({ inviteCode }) => ({
        url: "/company/join",
        method: "POST",
        body: { inviteCode },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["User", "Auth"],
    }),

    // Leave current company
    leaveCompany: builder.mutation({
      query: () => ({
        url: "/company/leave",
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["User", "Auth"],
    }),

    logout: builder.mutation({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
        // Refresh token is in HttpOnly cookie
      }),
      invalidatesTags: ["Auth", "User"],
    }),

    refreshToken: builder.mutation({
      query: () => ({
        url: "/auth/refresh-token",
        method: "POST",
        // Refresh token is in HttpOnly cookie
      }),
      transformResponse: (response) => response.data,
    }),

    getCurrentUser: builder.query({
      query: () => ({
        url: "/auth/me",
        method: "GET",
      }),
      transformResponse: (response) => response.data.user,
      providesTags: ["User"],
    }),

    // ==================== USER ENDPOINTS ====================

    updateProfile: builder.mutation({
      query: (profileData) => ({
        url: "/users/me",
        method: "PATCH",
        body: profileData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["User"],
    }),

    changePassword: builder.mutation({
      query: (passwordData) => ({
        url: "/users/me/password",
        method: "PATCH",
        body: passwordData,
      }),
      transformResponse: (response) => response.data,
    }),

    // ==================== ADMIN ENDPOINTS ====================

    getAllUsers: builder.query({
      query: ({ page = 1, limit = 10, search, role, isActive } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);
        if (role) params.append("role", role);
        if (isActive !== undefined) params.append("isActive", isActive);

        return {
          url: `/admin/users?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["Admin"],
    }),

    getUserById: builder.query({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.user,
      providesTags: ["Admin"],
    }),

    createUser: builder.mutation({
      query: (userData) => ({
        url: "/admin/users",
        method: "POST",
        body: userData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Admin"],
    }),

    updateUser: builder.mutation({
      query: ({ id, ...userData }) => ({
        url: `/admin/users/${id}`,
        method: "PUT",
        body: userData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Admin", "User"],
    }),

    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Admin"],
    }),

    resetUserPassword: builder.mutation({
      query: (id) => ({
        url: `/admin/users/${id}/reset-password`,
        method: "PATCH",
      }),
      transformResponse: (response) => response.data,
    }),

    // ==================== ADMIN COMPANY ENDPOINTS ====================

    getAllCompanies: builder.query({
      query: ({ page = 1, limit = 10, search, isActive, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);
        if (isActive !== undefined) params.append("isActive", isActive);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/admin/companies?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["Admin"],
    }),

    getCompanyById: builder.query({
      query: (id) => ({
        url: `/admin/companies/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.company,
      providesTags: ["Admin"],
    }),

    getCompanyMembers: builder.query({
      query: ({ id, page = 1, limit = 10, search } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);

        return {
          url: `/admin/companies/${id}/members?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["Admin"],
    }),

    deleteCompany: builder.mutation({
      query: (id) => ({
        url: `/admin/companies/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Admin"],
    }),

    // ==================== COMPANY POSITION ENDPOINTS ====================

    // Get all positions for a company
    getCompanyPositions: builder.query({
      query: (companyId) => ({
        url: `/company/${companyId}/positions`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.positions,
      providesTags: ["CompanyPosition"],
    }),

    // Create a new position
    createCompanyPosition: builder.mutation({
      query: ({ companyId, name, description }) => ({
        url: `/company/${companyId}/positions`,
        method: "POST",
        body: { name, description },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompanyPosition"],
    }),

    // Update a position
    updateCompanyPosition: builder.mutation({
      query: ({ companyId, positionId, name, description }) => ({
        url: `/company/${companyId}/positions/${positionId}`,
        method: "PUT",
        body: { name, description },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompanyPosition"],
    }),

    // Delete a position
    deleteCompanyPosition: builder.mutation({
      query: ({ companyId, positionId }) => ({
        url: `/company/${companyId}/positions/${positionId}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompanyPosition"],
    }),

    // Assign position to employee (admin assigns)
    assignEmployeePosition: builder.mutation({
      query: ({ companyId, userId, positionId }) => ({
        url: `/company/${companyId}/members/${userId}/position`,
        method: "PATCH",
        body: { positionId },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompanyPosition", "Admin"],
    }),

    // Employee selects their own position
    selectMyPosition: builder.mutation({
      query: ({ positionId }) => ({
        url: "/company/my-position",
        method: "PATCH",
        body: { positionId },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["User", "Auth", "CompanyPosition"],
    }),

    // Get team members (client-side - for company hosts)
    getMyTeamMembers: builder.query({
      query: ({ companyId, page = 1, limit = 50 }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        return {
          url: `/company/${companyId}/members?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["CompanyPosition", "Team"],
    }),

    // ==================== QUESTION BANK ENDPOINTS ====================

    getQuestions: builder.query({
      query: ({ page = 1, limit = 20, type, category, search, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (type) params.append("type", type);
        if (category) params.append("category", category);
        if (search) params.append("search", search);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/unpack-values/questions?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["QuestionBank"],
    }),

    getQuestionById: builder.query({
      query: (id) => ({
        url: `/unpack-values/questions/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.question,
      providesTags: ["QuestionBank"],
    }),

    getQuestionCategories: builder.query({
      query: () => ({
        url: "/unpack-values/questions/categories",
        method: "GET",
      }),
      transformResponse: (response) => response.data.categories,
      providesTags: ["QuestionBank"],
    }),

    getQuestionTags: builder.query({
      query: () => ({
        url: "/unpack-values/questions/tags",
        method: "GET",
      }),
      transformResponse: (response) => response.data.tags,
      providesTags: ["QuestionBank"],
    }),

    createQuestion: builder.mutation({
      query: (questionData) => ({
        url: "/unpack-values/questions",
        method: "POST",
        body: questionData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["QuestionBank"],
    }),

    updateQuestion: builder.mutation({
      query: ({ id, ...questionData }) => ({
        url: `/unpack-values/questions/${id}`,
        method: "PUT",
        body: questionData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["QuestionBank"],
    }),

    deleteQuestion: builder.mutation({
      query: (id) => ({
        url: `/unpack-values/questions/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["QuestionBank"],
    }),

    duplicateQuestion: builder.mutation({
      query: (id) => ({
        url: `/unpack-values/questions/${id}/duplicate`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["QuestionBank"],
    }),

    // ==================== FORM TEMPLATE ENDPOINTS ====================

    getFormTemplates: builder.query({
      query: ({ page = 1, limit = 20, status, search, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/unpack-values/templates?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["FormTemplate"],
    }),

    getFormTemplateById: builder.query({
      query: (id) => ({
        url: `/unpack-values/templates/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.template,
      providesTags: ["FormTemplate"],
    }),

    getTemplatesForCompany: builder.query({
      query: (companyId) => ({
        url: `/unpack-values/templates/company/${companyId}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.templates,
      providesTags: ["FormTemplate"],
    }),

    getTemplatesForEmployee: builder.query({
      query: ({ companyId, jobLevel }) => {
        const params = new URLSearchParams();
        if (jobLevel) params.append("jobLevel", jobLevel);

        return {
          url: `/unpack-values/templates/employee/${companyId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.templates,
      providesTags: ["FormTemplate"],
    }),

    createFormTemplate: builder.mutation({
      query: (templateData) => ({
        url: "/unpack-values/templates",
        method: "POST",
        body: templateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    updateFormTemplate: builder.mutation({
      query: ({ id, ...templateData }) => ({
        url: `/unpack-values/templates/${id}`,
        method: "PUT",
        body: templateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    deleteFormTemplate: builder.mutation({
      query: (id) => ({
        url: `/unpack-values/templates/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    publishFormTemplate: builder.mutation({
      query: (id) => ({
        url: `/unpack-values/templates/${id}/publish`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    archiveFormTemplate: builder.mutation({
      query: (id) => ({
        url: `/unpack-values/templates/${id}/archive`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    duplicateFormTemplate: builder.mutation({
      query: (id) => ({
        url: `/unpack-values/templates/${id}/duplicate`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    assignTemplateToCompany: builder.mutation({
      query: ({ templateId, companyId, jobLevels, deadline }) => ({
        url: `/unpack-values/templates/${templateId}/assign`,
        method: "POST",
        body: { companyId, jobLevels, deadline },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    unassignTemplateFromCompany: builder.mutation({
      query: ({ templateId, companyId }) => ({
        url: `/unpack-values/templates/${templateId}/unassign`,
        method: "POST",
        body: { companyId },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["FormTemplate"],
    }),

    // ==================== SUBMISSION ENDPOINTS ====================

    startSubmission: builder.mutation({
      query: ({ templateId, companyId }) => ({
        url: "/unpack-values/submissions/start",
        method: "POST",
        body: { templateId, companyId },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Submission"],
    }),

    getSubmissionById: builder.query({
      query: (id) => ({
        url: `/unpack-values/submissions/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.submission,
      providesTags: ["Submission"],
    }),

    getEmployeeSubmissions: builder.query({
      query: () => ({
        url: "/unpack-values/submissions/employee",
        method: "GET",
      }),
      transformResponse: (response) => response.data.submissions,
      providesTags: ["Submission"],
    }),

    getCompanySubmissions: builder.query({
      query: ({ companyId, page = 1, limit = 20, status, templateId, sortBy, includeAnswers = false } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (templateId) params.append("templateId", templateId);
        if (sortBy) params.append("sortBy", sortBy);
        if (includeAnswers) params.append("includeAnswers", "true");

        return {
          url: `/unpack-values/submissions/company/${companyId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["Submission"],
    }),

    getAllSubmissions: builder.query({
      query: ({ page = 1, limit = 20, status, templateId, employeeId, companyId, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (templateId) params.append("templateId", templateId);
        if (employeeId) params.append("employeeId", employeeId);
        if (companyId) params.append("companyId", companyId);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/unpack-values/submissions?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["Submission"],
    }),

    saveSubmissionProgress: builder.mutation({
      query: ({ id, answers, currentQuestionIndex, additionalTime }) => ({
        url: `/unpack-values/submissions/${id}/progress`,
        method: "PUT",
        body: { answers, currentQuestionIndex, additionalTime },
      }),
      transformResponse: (response) => response.data,
    }),

    updateSubmissionAnswer: builder.mutation({
      query: ({ id, body, questionIndex, questionId, value, answer, valueLabel, valueLabels }) => {
        // Support both formats: { id, body: {...} } and { id, questionIndex, value, ... }
        const requestBody = body || {
          questionIndex,
          questionId,
          value: value !== undefined ? value : answer,
          answer: answer !== undefined ? answer : value,
          valueLabel,
          valueLabels
        };
        return {
          url: `/unpack-values/submissions/${id}/answer`,
          method: "PUT",
          body: requestBody,
        };
      },
      transformResponse: (response) => response.data,
    }),

    submitForm: builder.mutation({
      query: ({ id, answers, timeSpent }) => ({
        url: `/unpack-values/submissions/${id}/submit`,
        method: "POST",
        body: { answers, timeSpent },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Submission"],
    }),

    markSubmissionReviewed: builder.mutation({
      query: ({ id, notes }) => ({
        url: `/unpack-values/submissions/${id}/review`,
        method: "POST",
        body: { notes },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Submission"],
    }),

    getTemplateStats: builder.query({
      query: (templateId) => ({
        url: `/unpack-values/submissions/template/${templateId}/stats`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.stats,
      providesTags: ["Submission"],
    }),

    getTemplateReport: builder.query({
      query: ({ templateId, companyId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append("companyId", companyId);

        return {
          url: `/unpack-values/submissions/template/${templateId}/report?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.answers,
      providesTags: ["Submission"],
    }),

    deleteSubmission: builder.mutation({
      query: (id) => ({
        url: `/unpack-values/submissions/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["Submission"],
    }),

    // ==================== DIAGNOSTIC TEMPLATE ENDPOINTS ====================

    getDiagnosticTemplates: builder.query({
      query: ({ page = 1, limit = 20, status, search, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/diagnostic/templates?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["DiagnosticTemplate"],
    }),

    getDiagnosticTemplateById: builder.query({
      query: (id) => ({
        url: `/diagnostic/templates/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.template,
      providesTags: ["DiagnosticTemplate"],
    }),

    getPublishedDiagnosticTemplates: builder.query({
      query: () => ({
        url: "/diagnostic/templates/published",
        method: "GET",
      }),
      transformResponse: (response) => response.data.templates,
      providesTags: ["DiagnosticTemplate"],
    }),

    createDiagnosticTemplate: builder.mutation({
      query: (templateData) => ({
        url: "/diagnostic/templates",
        method: "POST",
        body: templateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    updateDiagnosticTemplate: builder.mutation({
      query: ({ id, ...templateData }) => ({
        url: `/diagnostic/templates/${id}`,
        method: "PUT",
        body: templateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    deleteDiagnosticTemplate: builder.mutation({
      query: (id) => ({
        url: `/diagnostic/templates/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    publishDiagnosticTemplate: builder.mutation({
      query: (id) => ({
        url: `/diagnostic/templates/${id}/publish`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    archiveDiagnosticTemplate: builder.mutation({
      query: (id) => ({
        url: `/diagnostic/templates/${id}/archive`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    duplicateDiagnosticTemplate: builder.mutation({
      query: (id) => ({
        url: `/diagnostic/templates/${id}/duplicate`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    // Get templates assigned to a specific company
    getCompanyDiagnosticTemplates: builder.query({
      query: (companyId) => ({
        url: `/diagnostic/templates/company/${companyId}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.templates,
      providesTags: ["DiagnosticTemplate"],
    }),

    // Assign template to a company
    assignDiagnosticTemplateToCompany: builder.mutation({
      query: ({ templateId, companyId }) => ({
        url: `/diagnostic/templates/${templateId}/assign`,
        method: "POST",
        body: { companyId },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    // Unassign template from a company
    unassignDiagnosticTemplateFromCompany: builder.mutation({
      query: ({ templateId, companyId }) => ({
        url: `/diagnostic/templates/${templateId}/assign/${companyId}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticTemplate"],
    }),

    // ==================== DIAGNOSTIC ASSIGNMENT ENDPOINTS ====================

    createDiagnosticAssignment: builder.mutation({
      query: (assignmentData) => ({
        url: "/diagnostic/assignments",
        method: "POST",
        body: assignmentData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    getDiagnosticAssignmentById: builder.query({
      query: (id) => ({
        url: `/diagnostic/assignments/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.assignment,
      providesTags: ["DiagnosticAssignment"],
    }),

    getEmployeeDiagnosticAssignments: builder.query({
      query: () => ({
        url: "/diagnostic/assignments/employee",
        method: "GET",
      }),
      transformResponse: (response) => response.data.assignments,
      providesTags: ["DiagnosticAssignment"],
    }),

    getCompanyDiagnosticAssignments: builder.query({
      query: ({ companyId, page = 1, limit = 50, status, templateId } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (templateId) params.append("templateId", templateId);

        return {
          url: `/diagnostic/assignments/company/${companyId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["DiagnosticAssignment"],
    }),

    getCompanyDiagnosticStats: builder.query({
      query: (companyId) => ({
        url: `/diagnostic/assignments/company/${companyId}/stats`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.stats,
      providesTags: ["DiagnosticAssignment"],
    }),

    getTargetEvaluations: builder.query({
      query: ({ targetId, companyId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append("companyId", companyId);

        return {
          url: `/diagnostic/assignments/target/${targetId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.assignments,
      providesTags: ["DiagnosticAssignment"],
    }),

    updateDiagnosticAssignment: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/diagnostic/assignments/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    cancelDiagnosticAssignment: builder.mutation({
      query: (id) => ({
        url: `/diagnostic/assignments/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // ==================== DIAGNOSTIC REQUEST WORKFLOW ENDPOINTS ====================

    // Employee: Create diagnostic request (select diagnosers)
    createDiagnosticRequest: builder.mutation({
      query: ({ diagnoserIds, includeSelfDiagnosis }) => ({
        url: "/diagnostic/assignments/request",
        method: "POST",
        body: { diagnoserIds, includeSelfDiagnosis },
      }),
      transformResponse: (response) => response.data.request,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // Employee: Get my diagnostic request
    getMyDiagnosticRequest: builder.query({
      query: () => ({
        url: "/diagnostic/assignments/my-request",
        method: "GET",
      }),
      transformResponse: (response) => response.data.request,
      providesTags: ["DiagnosticAssignment"],
    }),

    // Employee: Update my diagnostic request (before approval)
    updateMyDiagnosticRequest: builder.mutation({
      query: ({ diagnoserIds, includeSelfDiagnosis }) => ({
        url: "/diagnostic/assignments/my-request",
        method: "PUT",
        body: { diagnoserIds, includeSelfDiagnosis },
      }),
      transformResponse: (response) => response.data.request,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // Admin: Get company diagnostic requests
    getCompanyDiagnosticRequests: builder.query({
      query: ({ companyId, status }) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        return {
          url: `/diagnostic/assignments/company/${companyId}/requests?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.requests,
      providesTags: ["DiagnosticAssignment"],
    }),

    // Admin: Approve diagnostic request
    approveDiagnosticRequest: builder.mutation({
      query: ({ requestId, templateId, modifications }) => ({
        url: `/diagnostic/assignments/request/${requestId}/approve`,
        method: "PUT",
        body: { templateId, modifications },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // Admin: Reject diagnostic request
    rejectDiagnosticRequest: builder.mutation({
      query: ({ requestId, reason }) => ({
        url: `/diagnostic/assignments/request/${requestId}/reject`,
        method: "PUT",
        body: { reason },
      }),
      transformResponse: (response) => response.data.request,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // Admin: Reassign diagnosers on approved request
    reassignDiagnosers: builder.mutation({
      query: ({ requestId, add, remove }) => ({
        url: `/diagnostic/assignments/request/${requestId}/reassign`,
        method: "PUT",
        body: { add, remove },
      }),
      transformResponse: (response) => response.data.request,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // Admin: Create diagnostic request for an employee (admin-initiated)
    createAdminDiagnosticRequest: builder.mutation({
      query: ({ companyId, targetId, diagnoserIds, templateId, includeSelfDiagnosis }) => ({
        url: "/diagnostic/assignments/admin-create",
        method: "POST",
        body: { companyId, targetId, diagnoserIds, templateId, includeSelfDiagnosis },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // Admin: Add additional template to existing request
    addTemplateToRequest: builder.mutation({
      query: ({ requestId, templateId }) => ({
        url: `/diagnostic/assignments/request/${requestId}/add-template`,
        method: "POST",
        body: { templateId },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticAssignment"],
    }),

    // ==================== DIAGNOSTIC SUBMISSION ENDPOINTS ====================

    startDiagnosticSubmission: builder.mutation({
      query: ({ assignmentId }) => ({
        url: "/diagnostic/submissions/start",
        method: "POST",
        body: { assignmentId },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticSubmission", "DiagnosticAssignment"],
    }),

    getDiagnosticSubmissionById: builder.query({
      query: (id) => ({
        url: `/diagnostic/submissions/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.submission,
      providesTags: ["DiagnosticSubmission"],
    }),

    getEmployeeDiagnosticSubmissions: builder.query({
      query: () => ({
        url: "/diagnostic/submissions/employee",
        method: "GET",
      }),
      transformResponse: (response) => response.data.submissions,
      providesTags: ["DiagnosticSubmission"],
    }),

    getCompanyDiagnosticSubmissions: builder.query({
      query: ({ companyId, page = 1, limit = 20, status, templateId, targetId, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);
        if (templateId) params.append("templateId", templateId);
        if (targetId) params.append("targetId", targetId);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/diagnostic/submissions/company/${companyId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["DiagnosticSubmission"],
    }),

    updateDiagnosticAnswer: builder.mutation({
      query: ({ id, questionIndex, value, valueLabel, valueLabels }) => ({
        url: `/diagnostic/submissions/${id}/answer`,
        method: "PUT",
        body: { questionIndex, value, valueLabel, valueLabels },
      }),
      transformResponse: (response) => response.data,
    }),

    saveDiagnosticProgress: builder.mutation({
      query: ({ id, answers, currentQuestionIndex, additionalTime }) => ({
        url: `/diagnostic/submissions/${id}/progress`,
        method: "PUT",
        body: { answers, currentQuestionIndex, additionalTime },
      }),
      transformResponse: (response) => response.data,
    }),

    submitDiagnosticForm: builder.mutation({
      query: (id) => ({
        url: `/diagnostic/submissions/${id}/submit`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticSubmission", "DiagnosticAssignment"],
    }),

    markDiagnosticReviewed: builder.mutation({
      query: ({ id, notes }) => ({
        url: `/diagnostic/submissions/${id}/review`,
        method: "POST",
        body: { notes },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticSubmission"],
    }),

    getTargetFeedback: builder.query({
      query: ({ targetId, companyId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append("companyId", companyId);

        return {
          url: `/diagnostic/submissions/target/${targetId}/feedback?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.submissions,
      providesTags: ["DiagnosticSubmission"],
    }),

    getTargetFeedbackReport: builder.query({
      query: ({ targetId, companyId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append("companyId", companyId);

        return {
          url: `/diagnostic/submissions/target/${targetId}/report?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.report,
      providesTags: ["DiagnosticSubmission"],
    }),

    getDiagnosticTemplateReport: builder.query({
      query: ({ templateId, companyId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append("companyId", companyId);

        return {
          url: `/diagnostic/submissions/template/${templateId}/report?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.report,
      providesTags: ["DiagnosticSubmission"],
    }),

    // Get current user's own aggregated feedback with stats
    getMyAggregatedFeedback: builder.query({
      query: ({ templateId } = {}) => {
        const params = new URLSearchParams();
        if (templateId) params.append("templateId", templateId);

        return {
          url: `/diagnostic/submissions/my-feedback/aggregated?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.report,
      providesTags: ["DiagnosticSubmission"],
    }),

    // Get aggregated feedback with stats about any target (admin)
    getTargetFeedbackWithStats: builder.query({
      query: ({ targetId, companyId, templateId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append("companyId", companyId);
        if (templateId) params.append("templateId", templateId);

        return {
          url: `/diagnostic/submissions/target/${targetId}/report/stats?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.report,
      providesTags: ["DiagnosticSubmission"],
    }),

    deleteDiagnosticSubmission: builder.mutation({
      query: (id) => ({
        url: `/diagnostic/submissions/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DiagnosticSubmission"],
    }),

    // ==================== LEARNING LIBRARY ENDPOINTS ====================

    // Admin endpoints
    getLearningResources: builder.query({
      query: ({ page = 1, limit = 20, type, search, isActive, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (type) params.append("type", type);
        if (search) params.append("search", search);
        if (isActive !== undefined) params.append("isActive", isActive);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/learning-library?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["LearningResource"],
    }),

    getLearningResourceById: builder.query({
      query: (id) => ({
        url: `/learning-library/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.resource,
      providesTags: ["LearningResource"],
    }),

    // Public endpoints (for client pages)
    getPublicLearningResources: builder.query({
      query: ({ type, search } = {}) => {
        const params = new URLSearchParams();
        if (type) params.append("type", type);
        if (search) params.append("search", search);

        return {
          url: `/learning-library/public?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data.resources,
      providesTags: ["LearningResource"],
    }),

    getPublicLearningResourceById: builder.query({
      query: (id) => ({
        url: `/learning-library/public/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.resource,
      providesTags: ["LearningResource"],
    }),

    createLearningResource: builder.mutation({
      query: (formData) => ({
        url: "/learning-library",
        method: "POST",
        body: formData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["LearningResource"],
    }),

    updateLearningResource: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/learning-library/${id}`,
        method: "PUT",
        body: formData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["LearningResource"],
    }),

    deleteLearningResource: builder.mutation({
      query: (id) => ({
        url: `/learning-library/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["LearningResource"],
    }),

    // ==================== DEVELOPMENT PLAN ENDPOINTS ====================

    // Get available values and their competencies (with development goals)
    getDevelopmentPlanValues: builder.query({
      query: () => ({
        url: "/development-plan/values",
        method: "GET",
      }),
      // Return full data object (includes source, competencySetId, competencySetName, values)
      transformResponse: (response) => response.data,
      providesTags: ["DevelopmentPlan"],
    }),

    // Get current user's development plan
    getMyDevelopmentPlan: builder.query({
      query: () => ({
        url: "/development-plan/my-plan",
        method: "GET",
      }),
      transformResponse: (response) => response.data.plan,
      providesTags: ["DevelopmentPlan"],
    }),

    // Create a new development plan
    createDevelopmentPlan: builder.mutation({
      query: (data) => ({
        url: "/development-plan",
        method: "POST",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DevelopmentPlan"],
    }),

    // Update current user's development plan
    updateDevelopmentPlan: builder.mutation({
      query: (data) => ({
        url: "/development-plan/my-plan",
        method: "PUT",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DevelopmentPlan"],
    }),

    // Update a single competency entry
    updateDevelopmentPlanEntry: builder.mutation({
      query: ({ competencyIndex, ...data }) => ({
        url: `/development-plan/my-plan/entry/${competencyIndex}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DevelopmentPlan"],
    }),

    // Delete current user's development plan
    deleteDevelopmentPlan: builder.mutation({
      query: () => ({
        url: "/development-plan/my-plan",
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["DevelopmentPlan"],
    }),

    // Admin: Get all plans for a company
    getCompanyDevelopmentPlans: builder.query({
      query: ({ companyId, page = 1, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);

        return {
          url: `/development-plan/company/${companyId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["DevelopmentPlan"],
    }),

    // ==================== COMPETENCY SET ENDPOINTS ====================

    // Get all competency sets (list view)
    getCompetencySets: builder.query({
      query: ({ page = 1, limit = 20, isActive, search, sortBy = "createdAt", sortOrder = "desc" } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (isActive !== undefined) params.append("isActive", isActive);
        if (search) params.append("search", search);
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return {
          url: `/competency-set?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["CompetencySet"],
    }),

    // Get single competency set by ID (full data with values)
    getCompetencySetById: builder.query({
      query: (id) => ({
        url: `/competency-set/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.competencySet,
      providesTags: ["CompetencySet"],
    }),

    // Create new competency set
    createCompetencySet: builder.mutation({
      query: (data) => ({
        url: "/competency-set",
        method: "POST",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // Update competency set
    updateCompetencySet: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/competency-set/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // Delete competency set
    deleteCompetencySet: builder.mutation({
      query: (id) => ({
        url: `/competency-set/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // Assign companies to competency set
    assignCompaniesToSet: builder.mutation({
      query: ({ id, companyIds }) => ({
        url: `/competency-set/${id}/assign`,
        method: "POST",
        body: { companyIds },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // Remove companies from competency set
    removeCompaniesFromSet: builder.mutation({
      query: ({ id, companyIds }) => ({
        url: `/competency-set/${id}/unassign`,
        method: "POST",
        body: { companyIds },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // Get competency set for current user's company
    getMyCompanyCompetencySet: builder.query({
      query: () => ({
        url: "/competency-set/my-company",
        method: "GET",
      }),
      transformResponse: (response) => response.data.competencySet,
      providesTags: ["CompetencySet"],
    }),

    // Get actions for a company's competency set (for form builder dropdown)
    getActionsForCompany: builder.query({
      query: (companyId) => ({
        url: `/competency-set/company/${companyId}/actions`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.actions,
      providesTags: ["CompetencySet"],
    }),

    // ==================== DEVELOPMENT GOAL ENDPOINTS ====================

    // Get all development goals for a competency set
    getDevelopmentGoals: builder.query({
      query: (setId) => ({
        url: `/competency-set/${setId}/development-goals`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.goals,
      providesTags: ["CompetencySet"],
    }),

    // Add a development goal
    addDevelopmentGoal: builder.mutation({
      query: ({ setId, ...data }) => ({
        url: `/competency-set/${setId}/development-goals`,
        method: "POST",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // Update a development goal
    updateDevelopmentGoal: builder.mutation({
      query: ({ setId, goalId, ...data }) => ({
        url: `/competency-set/${setId}/development-goals/${goalId}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // Delete a development goal
    deleteDevelopmentGoal: builder.mutation({
      query: ({ setId, goalId }) => ({
        url: `/competency-set/${setId}/development-goals/${goalId}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CompetencySet"],
    }),

    // ==================== COMPETENCY RUBRIC ENDPOINTS ====================

    // Get or create rubric for a company (Admin)
    getCompanyRubric: builder.query({
      query: (companyId) => ({
        url: `/competency-rubric/company/${companyId}`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.rubric,
      providesTags: ["CompetencyRubric"],
    }),

    // Get published rubric for user's company
    getMyCompanyRubric: builder.query({
      query: () => ({
        url: "/competency-rubric/my-company",
        method: "GET",
      }),
      transformResponse: (response) => response.data.rubric,
      providesTags: ["CompetencyRubric"],
    }),

    // Update a single cell rating (Admin)
    updateRubricCell: builder.mutation({
      query: ({ rubricId, ...cellData }) => ({
        url: `/competency-rubric/${rubricId}/cell`,
        method: "PUT",
        body: cellData,
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // Add a new role column (Admin)
    addRubricRole: builder.mutation({
      query: ({ rubricId, ...roleData }) => ({
        url: `/competency-rubric/${rubricId}/roles`,
        method: "POST",
        body: roleData,
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // Update a role column (Admin)
    updateRubricRole: builder.mutation({
      query: ({ rubricId, roleIndex, ...roleData }) => ({
        url: `/competency-rubric/${rubricId}/roles/${roleIndex}`,
        method: "PUT",
        body: roleData,
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // Remove a role column (Admin)
    removeRubricRole: builder.mutation({
      query: ({ rubricId, roleIndex }) => ({
        url: `/competency-rubric/${rubricId}/roles/${roleIndex}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // Add a custom rating tag (Admin)
    addRubricCustomTag: builder.mutation({
      query: ({ rubricId, ...tagData }) => ({
        url: `/competency-rubric/${rubricId}/custom-tags`,
        method: "POST",
        body: tagData,
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // Remove a custom rating tag (Admin)
    removeRubricCustomTag: builder.mutation({
      query: ({ rubricId, tagIndex }) => ({
        url: `/competency-rubric/${rubricId}/custom-tags/${tagIndex}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // Publish rubric (Admin)
    publishRubric: builder.mutation({
      query: (rubricId) => ({
        url: `/competency-rubric/${rubricId}/publish`,
        method: "PUT",
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // Unpublish rubric (Admin)
    unpublishRubric: builder.mutation({
      query: (rubricId) => ({
        url: `/competency-rubric/${rubricId}/unpublish`,
        method: "PUT",
      }),
      transformResponse: (response) => response.data.rubric,
      invalidatesTags: ["CompetencyRubric"],
    }),

    // ==================== COMMUNITY EVENT ENDPOINTS ====================

    // Admin: Get all community events
    getCommunityEvents: builder.query({
      query: ({ page = 1, limit = 20, cadence, category, search, isActive, sortBy } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (cadence) params.append("cadence", cadence);
        if (category) params.append("category", category);
        if (search) params.append("search", search);
        if (isActive !== undefined) params.append("isActive", isActive);
        if (sortBy) params.append("sortBy", sortBy);

        return {
          url: `/community?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["CommunityEvent"],
    }),

    // Admin: Create community event
    createCommunityEvent: builder.mutation({
      query: (eventData) => ({
        url: "/community",
        method: "POST",
        body: eventData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CommunityEvent"],
    }),

    // Admin: Update community event
    updateCommunityEvent: builder.mutation({
      query: ({ id, ...eventData }) => ({
        url: `/community/${id}`,
        method: "PUT",
        body: eventData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CommunityEvent"],
    }),

    // Admin: Delete community event
    deleteCommunityEvent: builder.mutation({
      query: (id) => ({
        url: `/community/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CommunityEvent"],
    }),

    // Admin: Update community event with scope (for recurring events)
    updateCommunityEventWithScope: builder.mutation({
      query: ({ id, scope = 'all', ...eventData }) => ({
        url: `/community/${id}/with-scope`,
        method: "PUT",
        body: { ...eventData, scope },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CommunityEvent"],
    }),

    // Admin: Get event instances (for recurring events)
    getEventInstances: builder.query({
      query: ({ eventId, status, page = 1, limit = 50 } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (status) params.append("status", status);

        return {
          url: `/community/${eventId}/instances?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["CommunityEvent"],
    }),

    // Admin: Cancel a single occurrence
    cancelOccurrence: builder.mutation({
      query: (instanceId) => ({
        url: `/community/${instanceId}/cancel`,
        method: "PATCH",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CommunityEvent"],
    }),

    // Admin: Restore a cancelled occurrence
    restoreOccurrence: builder.mutation({
      query: (instanceId) => ({
        url: `/community/${instanceId}/restore`,
        method: "PATCH",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["CommunityEvent"],
    }),

    // Admin: Get accepted RSVPs for event
    getEventRSVPs: builder.query({
      query: (eventId) => ({
        url: `/community/${eventId}/rsvps`,
        method: "GET",
      }),
      transformResponse: (response) => response.data,
      providesTags: ["RSVP"],
    }),

    // Admin: Get pending RSVPs for event (approval queue)
    getPendingRSVPs: builder.query({
      query: (eventId) => ({
        url: `/community/${eventId}/rsvps/pending`,
        method: "GET",
      }),
      transformResponse: (response) => response.data,
      providesTags: ["RSVP"],
    }),

    // Admin: Accept RSVP request
    acceptRSVP: builder.mutation({
      query: ({ eventId, rsvpId }) => ({
        url: `/community/${eventId}/rsvps/${rsvpId}/accept`,
        method: "PATCH",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["RSVP", "CommunityEvent"],
    }),

    // Admin: Decline RSVP request (hides event from user)
    declineRSVP: builder.mutation({
      query: ({ eventId, rsvpId }) => ({
        url: `/community/${eventId}/rsvps/${rsvpId}/decline`,
        method: "PATCH",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["RSVP", "CommunityEvent"],
    }),

    // Admin: Get employees for a company (for event employee selection)
    getCompanyEmployeesForEvent: builder.query({
      query: (companyId) => ({
        url: `/community/companies/${companyId}/employees`,
        method: "GET",
      }),
      transformResponse: (response) => response.data.employees,
      providesTags: ["Admin"],
    }),

    // Public: Get community events (for clients)
    getPublicCommunityEvents: builder.query({
      query: ({ page = 1, limit = 20, cadence, category, search } = {}) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (cadence) params.append("cadence", cadence);
        if (category) params.append("category", category);
        if (search) params.append("search", search);

        return {
          url: `/community/public?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response.data,
      providesTags: ["CommunityEvent"],
    }),

    // Client: Create RSVP (submits as pending for admin approval)
    createRSVP: builder.mutation({
      query: (eventId) => ({
        url: `/community/public/${eventId}/rsvp`,
        method: "POST",
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ["RSVP", "CommunityEvent"],
    }),

    // Client: Get my RSVPs
    getMyRSVPs: builder.query({
      query: () => ({
        url: "/community/my-rsvps",
        method: "GET",
      }),
      transformResponse: (response) => response.data.rsvps,
      providesTags: ["RSVP"],
    }),
  }),
});

// Export hooks for usage in components
export const {
  // Auth hooks
  useLoginMutation,
  useRegisterMutation,
  useRegisterWithCompanyMutation,
  useJoinWithCodeMutation,
  useValidateInviteCodeQuery,
  useCreateCompanyForUserMutation,
  useJoinCompanyForUserMutation,
  useLeaveCompanyMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
  useGetCurrentUserQuery,

  // User hooks
  useUpdateProfileMutation,
  useChangePasswordMutation,

  // Admin User hooks
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetUserPasswordMutation,

  // Admin Company hooks
  useGetAllCompaniesQuery,
  useGetCompanyByIdQuery,
  useGetCompanyMembersQuery,
  useDeleteCompanyMutation,

  // Company Position hooks
  useGetCompanyPositionsQuery,
  useCreateCompanyPositionMutation,
  useUpdateCompanyPositionMutation,
  useDeleteCompanyPositionMutation,
  useAssignEmployeePositionMutation,
  useSelectMyPositionMutation,
  useGetMyTeamMembersQuery,

  // Question Bank hooks
  useGetQuestionsQuery,
  useGetQuestionByIdQuery,
  useGetQuestionCategoriesQuery,
  useGetQuestionTagsQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useDuplicateQuestionMutation,

  // Form Template hooks
  useGetFormTemplatesQuery,
  useGetFormTemplateByIdQuery,
  useLazyGetFormTemplateByIdQuery,
  useGetTemplatesForCompanyQuery,
  useGetTemplatesForEmployeeQuery,
  useCreateFormTemplateMutation,
  useUpdateFormTemplateMutation,
  useDeleteFormTemplateMutation,
  usePublishFormTemplateMutation,
  useArchiveFormTemplateMutation,
  useDuplicateFormTemplateMutation,
  useAssignTemplateToCompanyMutation,
  useUnassignTemplateFromCompanyMutation,

  // Submission hooks
  useStartSubmissionMutation,
  useGetSubmissionByIdQuery,
  useGetEmployeeSubmissionsQuery,
  useGetCompanySubmissionsQuery,
  useGetAllSubmissionsQuery,
  useSaveSubmissionProgressMutation,
  useUpdateSubmissionAnswerMutation,
  useSubmitFormMutation,
  useMarkSubmissionReviewedMutation,
  useGetTemplateStatsQuery,
  useGetTemplateReportQuery,
  useDeleteSubmissionMutation,

  // Diagnostic Template hooks
  useGetDiagnosticTemplatesQuery,
  useGetDiagnosticTemplateByIdQuery,
  useLazyGetDiagnosticTemplateByIdQuery,
  useGetPublishedDiagnosticTemplatesQuery,
  useCreateDiagnosticTemplateMutation,
  useUpdateDiagnosticTemplateMutation,
  useDeleteDiagnosticTemplateMutation,
  usePublishDiagnosticTemplateMutation,
  useArchiveDiagnosticTemplateMutation,
  useDuplicateDiagnosticTemplateMutation,
  useGetCompanyDiagnosticTemplatesQuery,
  useAssignDiagnosticTemplateToCompanyMutation,
  useUnassignDiagnosticTemplateFromCompanyMutation,

  // Diagnostic Assignment hooks
  useCreateDiagnosticAssignmentMutation,
  useGetDiagnosticAssignmentByIdQuery,
  useGetEmployeeDiagnosticAssignmentsQuery,
  useGetCompanyDiagnosticAssignmentsQuery,
  useGetCompanyDiagnosticStatsQuery,
  useGetTargetEvaluationsQuery,
  useUpdateDiagnosticAssignmentMutation,
  useCancelDiagnosticAssignmentMutation,

  // Diagnostic Request Workflow hooks
  useCreateDiagnosticRequestMutation,
  useGetMyDiagnosticRequestQuery,
  useUpdateMyDiagnosticRequestMutation,
  useGetCompanyDiagnosticRequestsQuery,
  useApproveDiagnosticRequestMutation,
  useRejectDiagnosticRequestMutation,
  useReassignDiagnosersMutation,
  useCreateAdminDiagnosticRequestMutation,
  useAddTemplateToRequestMutation,

  // Diagnostic Submission hooks
  useStartDiagnosticSubmissionMutation,
  useGetDiagnosticSubmissionByIdQuery,
  useGetEmployeeDiagnosticSubmissionsQuery,
  useGetCompanyDiagnosticSubmissionsQuery,
  useUpdateDiagnosticAnswerMutation,
  useSaveDiagnosticProgressMutation,
  useSubmitDiagnosticFormMutation,
  useMarkDiagnosticReviewedMutation,
  useGetTargetFeedbackQuery,
  useGetTargetFeedbackReportQuery,
  useGetDiagnosticTemplateReportQuery,
  useGetMyAggregatedFeedbackQuery,
  useLazyGetMyAggregatedFeedbackQuery,
  useGetTargetFeedbackWithStatsQuery,
  useDeleteDiagnosticSubmissionMutation,

  // Learning Library hooks
  useGetLearningResourcesQuery,
  useGetLearningResourceByIdQuery,
  useGetPublicLearningResourcesQuery,
  useGetPublicLearningResourceByIdQuery,
  useCreateLearningResourceMutation,
  useUpdateLearningResourceMutation,
  useDeleteLearningResourceMutation,

  // Development Plan hooks
  useGetDevelopmentPlanValuesQuery,
  useGetMyDevelopmentPlanQuery,
  useCreateDevelopmentPlanMutation,
  useUpdateDevelopmentPlanMutation,
  useUpdateDevelopmentPlanEntryMutation,
  useDeleteDevelopmentPlanMutation,
  useGetCompanyDevelopmentPlansQuery,

  // Competency Set hooks
  useGetCompetencySetsQuery,
  useGetCompetencySetByIdQuery,
  useLazyGetCompetencySetByIdQuery,
  useCreateCompetencySetMutation,
  useUpdateCompetencySetMutation,
  useDeleteCompetencySetMutation,
  useAssignCompaniesToSetMutation,
  useRemoveCompaniesFromSetMutation,
  useGetMyCompanyCompetencySetQuery,
  useGetActionsForCompanyQuery,

  // Development Goal hooks
  useGetDevelopmentGoalsQuery,
  useAddDevelopmentGoalMutation,
  useUpdateDevelopmentGoalMutation,
  useDeleteDevelopmentGoalMutation,

  // Competency Rubric hooks
  useGetCompanyRubricQuery,
  useGetMyCompanyRubricQuery,
  useUpdateRubricCellMutation,
  useAddRubricRoleMutation,
  useUpdateRubricRoleMutation,
  useRemoveRubricRoleMutation,
  useAddRubricCustomTagMutation,
  useRemoveRubricCustomTagMutation,
  usePublishRubricMutation,
  useUnpublishRubricMutation,

  // Community Event hooks
  useGetCommunityEventsQuery,
  useCreateCommunityEventMutation,
  useUpdateCommunityEventMutation,
  useDeleteCommunityEventMutation,
  useUpdateCommunityEventWithScopeMutation,
  useGetEventInstancesQuery,
  useCancelOccurrenceMutation,
  useRestoreOccurrenceMutation,
  useGetEventRSVPsQuery,
  useGetPendingRSVPsQuery,
  useAcceptRSVPMutation,
  useDeclineRSVPMutation,
  useGetCompanyEmployeesForEventQuery,
  useGetPublicCommunityEventsQuery,
  useCreateRSVPMutation,
  useGetMyRSVPsQuery,
} = api;
